import { CalendarRange, PlugZap, Users } from "lucide-react";
import type * as React from "react";
import { useMemo, useState } from "react";
import { useSetBreadcrumbs } from "@/components/shared/breadcrumbs";
import { DataTable } from "@/components/shared/data-table";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { FilterToggleGroup } from "@/components/shared/filter-toggle-group";
import { interactiveRowProps } from "@/components/shared/interactive-row";
import { PageHeader } from "@/components/shared/page-header";
import { SearchInput } from "@/components/shared/search-input";
import { StatusPill } from "@/components/shared/status-badge";
import { useTableSort } from "@/components/shared/use-table-sort";
import { Skeleton } from "@/components/ui/skeleton";
import { usePeopleTargets, usePersonPeriods } from "@/hooks/use-people-targets";
import {
  formatCapacity,
  formatPeriodLabel,
  formatShortDate,
  type PeopleTargetPerson,
  type PeopleTargetsSummary,
  type PersonPeriodTarget,
  sortPeople,
  sortPeriods,
  targetPercent,
  UNSET,
} from "@/lib/people-targets";
import { cn } from "@/lib/utils";

const ACTIVE_FILTERS = [
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
];

/**
 * Active by default. NOS keeps every leaver, so two thirds of the unfiltered
 * list (69 of 157 in dev) is people who no longer work here — the question
 * this report answers is almost always about the ones who do. The chips make
 * the other views one click away, and the tiles always show both counts so the
 * filter can never hide how many people there are.
 */
const DEFAULT_FILTER = ["active"];

/**
 * Passed to each panel's `DataTable` so ITS wrapper is the scrollport.
 *
 * This has to live on the table's own box, not on a div around it: DataTable's
 * root is `overflow-x-auto`, and per CSS an `overflow-y: visible` alongside
 * that computes to `auto` — so the table's wrapper is already a scroll
 * container and is therefore the nearest scrollport its `position: sticky`
 * header resolves against. A scroll box wrapped AROUND it scrolls the whole
 * table, header included, while the sticky header sits pinned to a container
 * that never moves — which is exactly the way it silently failed.
 *
 * `bg-muted` on the header cells is load-bearing too: the header row's own
 * `bg-muted/50` is semi-transparent, so rows would show through it as they
 * pass underneath. `contain-content` is the fix for the document-inflation bug
 * described on the panel below.
 */
const TABLE_SCROLL =
  "min-h-0 flex-1 overflow-auto rounded-none border-0 border-t contain-content [&_thead_th]:sticky [&_thead_th]:top-0 [&_thead_th]:z-10 [&_thead_th]:bg-muted";

/** The tiles, in order. Doubles as the key set for their loading skeletons. */
const TILE_LABELS = [
  "People in NOS",
  "Active",
  "Avg capacity",
  "Avg target",
  "In scope for VC",
] as const;

/**
 * Administration → Capacity. NOS's own People Targets screen, read-only:
 * the people on the left, the selected person's monthly capacity and target
 * utilization on the right, with current-month totals across the top.
 *
 * Master/detail rather than one flat table because the detail feed is
 * per-person — `PersonPeriod` is 4,882 rows over 142 people, and a report that
 * renders one person at a time should not pull all of it. Selecting a person
 * costs one gateway call for their ~34 months.
 *
 * Nothing here writes. NOS owns these numbers; Enable only shows them.
 */
export function PeopleTargetsPage() {
  useSetBreadcrumbs([{ label: "Administration" }, { label: "Capacity" }]);

  const [personSearch, setPersonSearch] = useState("");
  const [activeFilter, setActiveFilter] = useState<string[]>(DEFAULT_FILTER);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const { sort, toggleSort } = useTableSort("name");

  const { data, isLoading, isError, error, refetch } = usePeopleTargets();

  const all = data?.people ?? [];
  const people = useMemo(
    () => sortPeople(filterPeople(all, personSearch, activeFilter), sort),
    [all, personSearch, activeFilter, sort],
  );

  // A person filtered out of the list must not keep the detail panel — the
  // right-hand grid would then describe someone no longer on screen.
  const selected = people.find((p) => p.id === selectedId) ?? null;

  return (
    // data-wide-page: two grids side by side (five columns and four) need the
    // full window — inside the default 72rem container the people table starts
    // scrolling sideways and hides its Active column. See DashboardLayout.
    <div className="space-y-6" data-wide-page>
      {/* The title stays pinned to the top of the scrolling main area while
          the body scrolls beneath, matching PersonProfile — see the note
          there for why this shape is what it is: the negative margins let the
          backdrop span main's padding, the background must be fully OPAQUE or
          rows ghost through it, and the ::before covers main's top padding,
          which a stuck box cannot be pulled over with a negative margin
          because it is clamped to the scrollport's padding box. The filler
          heights mirror main's p-4 / md:p-6.

          Only the title pins, not the tiles: pinning ~190px of a panel that
          is itself only 32rem tall would cost more of the tables than it
          gives back. Each table's own header is already sticky inside its
          panel, so the column labels never leave either. */}
      <div className="sticky top-0 z-20 -mx-4 bg-background px-4 pb-3 pt-1 before:absolute before:inset-x-0 before:bottom-full before:h-4 before:bg-background md:-mx-6 md:px-6 md:before:h-6">
        <PageHeader
          eyebrow="Administration"
          title="Capacity"
          description="Monthly capacity and target utilization per person, read live from NOS."
        />
      </div>

      {isError && (
        <ErrorState
          message={error instanceof Error ? error.message : "Could not load Capacity"}
          onRetry={() => {
            void refetch();
          }}
        />
      )}

      {data && !data.connected && (
        <EmptyState
          icon={PlugZap}
          title="Not connected to NOS"
          description="These targets are read live from the Nymbl Gateway. This environment doesn't have gateway access configured."
        />
      )}

      {(isLoading || data?.connected) && (
        <>
          <SummaryTiles people={all} summary={data?.summary ?? null} loading={isLoading} />

          {/* Asymmetric on purpose: the people grid carries five columns (two
              of them dates) and needs ~670px before it starts scrolling
              sideways, where the periods grid's four fit comfortably in less. */}
          <div className="grid gap-4 lg:grid-cols-[minmax(0,7fr)_minmax(0,5fr)]">
            <Panel
              icon={Users}
              title="People"
              count={isLoading ? null : people.length}
              toolbar={
                <div className="space-y-2">
                  <SearchInput
                    value={personSearch}
                    onValueChange={setPersonSearch}
                    placeholder="Search people…"
                  />
                  {/* Labelled group: the chips read "All / Active / Inactive",
                      and the people table has an "Active" column header too —
                      without this, both are just a button called "Active" to
                      anyone navigating by accessible name. */}
                  <fieldset
                    aria-label="Filter by employment status"
                    className="flex flex-wrap items-center gap-1.5"
                  >
                    <FilterToggleGroup
                      options={ACTIVE_FILTERS}
                      value={activeFilter}
                      onChange={setActiveFilter}
                    />
                  </fieldset>
                </div>
              }
            >
              <DataTable
                headers={[
                  { label: "Person", sortKey: "name" },
                  { label: "Start", sortKey: "startDate" },
                  { label: "End", sortKey: "endDate", className: "hidden sm:table-cell" },
                  { label: "Schedule", sortKey: "scheduleType" },
                  { label: "Active", sortKey: "active" },
                ]}
                className={TABLE_SCROLL}
                loading={isLoading}
                sort={sort}
                onSort={toggleSort}
                storageKey="people-targets-people"
                empty={
                  <EmptyState
                    icon={Users}
                    title="No people match"
                    description={
                      activeFilter.length === 0
                        ? "Clear the search to see everyone NOS knows."
                        : "Clear the search, or switch the filter to All."
                    }
                  />
                }
              >
                {people.map((person) => {
                  const row = interactiveRowProps(
                    () => setSelectedId(person.id),
                    `Show ${person.name}`,
                  );
                  return (
                    <tr
                      key={person.id}
                      {...row}
                      aria-current={person.id === selectedId ? "true" : undefined}
                      // The selection is what says whose months are on the
                      // right, so it needs to be legible at a glance: a tinted
                      // row plus a left accent bar, which survives the hover
                      // state the way a background alone doesn't.
                      className={cn(
                        row.className,
                        person.id === selectedId &&
                          "bg-primary/[0.07] font-medium text-foreground shadow-[inset_3px_0_0_var(--color-primary)] hover:bg-primary/[0.07]",
                      )}
                    >
                      <td className="px-4 py-2.5 font-medium text-foreground">{person.name}</td>
                      <td className="px-4 py-2.5 tabular-nums text-muted-foreground">
                        {formatShortDate(person.startDate)}
                      </td>
                      <td className="hidden sm:table-cell px-4 py-2.5 tabular-nums text-muted-foreground">
                        {formatShortDate(person.endDate)}
                      </td>
                      <td className="px-4 py-2.5 text-muted-foreground">
                        {person.scheduleType ?? UNSET}
                      </td>
                      <td className="px-4 py-2.5">
                        <BoolPill value={person.active} />
                      </td>
                    </tr>
                  );
                })}
              </DataTable>
            </Panel>

            <PeriodsPanel person={selected} />
          </div>
        </>
      )}
    </div>
  );
}

/**
 * The detail half. Its own component so the periods query unmounts with it —
 * and so the four states it can be in (nobody selected, loading, failed, no
 * targets recorded) each get said out loud rather than sharing one blank grid.
 */
function PeriodsPanel({ person }: { person: PeopleTargetPerson | null }) {
  const [search, setSearch] = useState("");
  // Newest month first: the question asked of a person's targets is almost
  // always about the months ahead, and NOS holds rows out to 2027.
  const { sort, toggleSort } = useTableSort("periodNumber", "desc");
  const { data, isLoading, isError, error, refetch } = usePersonPeriods(person?.id ?? null);

  const periods = useMemo(
    () => sortPeriods(filterPeriods(data?.periods ?? [], search), sort),
    [data, search, sort],
  );

  return (
    <Panel
      icon={CalendarRange}
      title={person === null ? "Person periods" : `Person periods — ${person.name}`}
      count={person === null || isLoading ? null : periods.length}
      toolbar={
        <SearchInput value={search} onValueChange={setSearch} placeholder="Search periods…" />
      }
    >
      {person === null ? (
        <EmptyState
          icon={Users}
          title="Select a person"
          description="Pick someone on the left to see the capacity and target NOS holds for each of their months."
        />
      ) : isError ? (
        <ErrorState
          message={error instanceof Error ? error.message : "Could not load these targets"}
          onRetry={() => {
            void refetch();
          }}
        />
      ) : (
        <DataTable
          headers={[
            { label: "Period", sortKey: "periodNumber" },
            { label: "Capacity", sortKey: "capacityHours", className: "text-right" },
            { label: "Target", sortKey: "targetUtilization", className: "text-right" },
            { label: "In scope", sortKey: "inScopeForVc" },
          ]}
          className={TABLE_SCROLL}
          loading={isLoading}
          sort={sort}
          onSort={toggleSort}
          storageKey="people-targets-periods"
          empty={
            <EmptyState
              icon={CalendarRange}
              title={search.trim() === "" ? "No targets set" : "No periods match"}
              description={
                search.trim() === ""
                  ? `NOS has no monthly capacity recorded for ${person.name}.`
                  : "Clear the search to see every month."
              }
            />
          }
        >
          {periods.map((period) => (
            <tr
              key={period.periodNumber}
              className="border-b border-border last:border-0 even:bg-muted/40"
            >
              <td className="px-4 py-2.5 font-medium text-foreground" title={period.periodName}>
                {formatPeriodLabel(period.periodNumber)}
              </td>
              <td className="px-4 py-2.5 text-right tabular-nums text-foreground">
                {formatCapacity(period.capacityHours)}
              </td>
              <td className="px-4 py-2.5 text-right tabular-nums text-muted-foreground">
                {targetPercent(period.targetUtilization)}
              </td>
              <td className="px-4 py-2.5">
                <BoolPill value={period.inScopeForVc} />
              </td>
            </tr>
          ))}
        </DataTable>
      )}
    </Panel>
  );
}

/**
 * Current-month totals across the top. The first two come from the people list
 * and are always available; the last three come from a separate SQL aggregate
 * and are labelled with the period they describe, because NOS's "current"
 * period is its own open month and not necessarily today's.
 *
 * Each average names the rows it covers when that is fewer than the people
 * with a row that month — 81 of 88 have a capacity in dev, and an average
 * presented as covering everyone would overstate its own basis.
 */
function SummaryTiles({
  people,
  summary,
  loading,
}: {
  people: PeopleTargetPerson[];
  summary: PeopleTargetsSummary | null;
  loading: boolean;
}) {
  if (loading) {
    return (
      <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 lg:grid-cols-5">
        {TILE_LABELS.map((label) => (
          <Skeleton key={label} className="h-[74px] rounded-[10px]" />
        ))}
      </div>
    );
  }

  const active = people.filter((p) => p.active).length;
  const period = summary?.periodName ?? null;
  const coverage = (covered: number) =>
    summary !== null && covered < summary.peopleWithTargets
      ? `${period} · ${covered} of ${summary.peopleWithTargets}`
      : period;

  return (
    <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 lg:grid-cols-5">
      <StatTile value={String(people.length)} label={TILE_LABELS[0]} />
      <StatTile value={String(active)} label={TILE_LABELS[1]} tone="text-success-foreground" />
      <StatTile
        value={summary?.avgCapacity == null ? UNSET : formatCapacity(summary.avgCapacity)}
        label={TILE_LABELS[2]}
        hint={summary === null ? "No open period" : coverage(summary.withCapacity)}
      />
      <StatTile
        value={targetPercent(summary?.avgTarget ?? null)}
        label={TILE_LABELS[3]}
        hint={summary === null ? "No open period" : coverage(summary.withTarget)}
      />
      <StatTile
        value={summary === null ? UNSET : String(summary.inScopeCount)}
        label={TILE_LABELS[4]}
        // "of 88" leads here because the tile's own figure is the numerator,
        // where the two averages above it are a single number their coverage
        // then qualifies.
        hint={summary === null ? "No open period" : `of ${summary.peopleWithTargets} · ${period}`}
      />
    </div>
  );
}

/**
 * One labelled figure. Deliberately mirrors `dashboard/OrgHealthTiles`'
 * `StatTile` — same radius, type scale and muted label — so the two admin
 * reports read as one system; it takes a formatted STRING rather than that
 * one's `number` because these are hours and percentages, not counts.
 */
function StatTile({
  value,
  label,
  hint,
  tone,
}: {
  value: string;
  label: string;
  hint?: string | null;
  tone?: string;
}) {
  return (
    <div className="flex flex-col rounded-[10px] border bg-card px-3.5 py-3">
      <span
        className={cn(
          "font-display text-2xl font-bold leading-none tabular-nums text-card-foreground",
          tone,
        )}
      >
        {value}
      </span>
      <span className="mt-1.5 text-2xs font-medium leading-tight text-muted-foreground">
        {label}
      </span>
      {hint != null && hint !== "" && (
        <span className="mt-0.5 text-3xs leading-tight text-muted-foreground/70">{hint}</span>
      )}
    </div>
  );
}

/** One side of the report: a titled, counted card wrapping a scrollable table. */
function Panel({
  icon: Icon,
  title,
  count,
  toolbar,
  children,
}: {
  icon: typeof Users;
  title: string;
  count: number | null;
  toolbar: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    // Sized to the window from `lg` up, where the two panels sit side by side:
    // the page then never scrolls, so each panel scrolls its own rows and its
    // column headers stay on screen. Capping the SECTION rather than the
    // scroll area inside it means one number covers both panels even though
    // their toolbars differ in height (the people panel carries filter chips),
    // and both still end at the same line. The 21.5rem reserves the chrome
    // above — top bar, the pinned title, the tiles and the gaps between them —
    // and is measured, not guessed: at 21rem the page overflowed by exactly
    // 8px at every height, which is enough to raise a scrollbar over nothing.
    // Below `lg` the panels stack, so they keep a fixed cap and the page
    // scrolls between them — that is what the pinned title is for.
    <section className="flex min-w-0 max-h-[36rem] flex-col overflow-hidden rounded-xl border bg-card shadow-sm lg:max-h-[calc(100dvh-21.5rem)] lg:min-h-[22rem]">
      <div className="flex items-center justify-between gap-3 border-b bg-accent px-4 py-3">
        <h2 className="flex min-w-0 items-center gap-2 font-display text-base font-semibold text-primary">
          <Icon className="size-4 shrink-0" />
          <span className="truncate">{title}</span>
        </h2>
        {count !== null && (
          <span className="shrink-0 rounded-full bg-card px-2 py-0.5 text-xs text-muted-foreground tabular-nums">
            {count} {count === 1 ? "row" : "rows"}
          </span>
        )}
      </div>
      <div className="px-4 py-3">{toolbar}</div>
      {/* Not a scroll box — the table inside is (see TABLE_SCROLL). This just
          hands it a bounded flex column to fill, so the panel's height is
          what decides how many rows are visible. */}
      <div className="flex min-h-0 flex-1 flex-col">{children}</div>
    </section>
  );
}

/**
 * A yes/no flag as a pill, for NOS's `IsActive` and `InScopeForVC` bits.
 *
 * A pill rather than a tick because both columns are the ones people scan for,
 * and a tick-or-dash makes "no" look like "missing". Labels stay at Yes/No so
 * the column is narrow enough that neither table needs a sideways scroll.
 */
function BoolPill({ value }: { value: boolean }) {
  return <StatusPill tone={value ? "success" : "neutral"} label={value ? "Yes" : "No"} />;
}

/** Search over name, plus the Active chips. An empty filter means everyone. */
function filterPeople(
  people: PeopleTargetPerson[],
  search: string,
  activeFilter: string[],
): PeopleTargetPerson[] {
  const term = search.trim().toLowerCase();
  return people.filter((person) => {
    if (term !== "" && !person.name.toLowerCase().includes(term)) return false;
    if (activeFilter.length === 0) return true;
    return activeFilter.includes(person.active ? "active" : "inactive");
  });
}

/**
 * Search over the month. Matches NOS's full name and the raw `YYYYMM` as well
 * as what's displayed, so "September", "Sep" and "202509" all find the row.
 */
function filterPeriods(periods: PersonPeriodTarget[], search: string): PersonPeriodTarget[] {
  const term = search.trim().toLowerCase();
  if (term === "") return periods;
  return periods.filter(
    (p) =>
      p.periodName.toLowerCase().includes(term) ||
      formatPeriodLabel(p.periodNumber).toLowerCase().includes(term) ||
      String(p.periodNumber).includes(term),
  );
}
